import React from 'react';
import { DollarSign, TrendingUp, AlertCircle } from 'lucide-react';
import { MonthlyForecast } from '../../lib/forecastService';

interface ForecastTableProps {
  forecasts: MonthlyForecast[];
}

const ForecastTable: React.FC<ForecastTableProps> = ({ forecasts }) => {
  if (forecasts.length === 0) {
    return null;
  }

  const hasChurnAdjustment = forecasts.some(f => f.churn_adjusted_total_revenue !== undefined);

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatMonth = (monthKey: string) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Detailed Forecast</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Month
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                Recurring
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                Prorated
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                Projects
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                {hasChurnAdjustment ? 'Baseline Total' : 'Total'}
              </th>
              {hasChurnAdjustment && (
                <th className="px-6 py-3 text-right text-xs font-medium text-orange-700 uppercase tracking-wider">
                  Churn Adjusted
                </th>
              )}
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                Confirmed
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                At Risk
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {forecasts.map((forecast, index) => {
              const isCurrentMonth = index === 0;
              const confidenceLevel = forecast.total_revenue > 0
                ? (forecast.confirmed_revenue / forecast.total_revenue) * 100
                : 100;

              return (
                <tr
                  key={forecast.month}
                  className={`hover:bg-gray-50 transition-colors ${
                    isCurrentMonth ? 'bg-blue-50' : ''
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {formatMonth(forecast.month)}
                      </span>
                      {isCurrentMonth && (
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          Current
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-gray-900">{formatCurrency(forecast.recurring_revenue)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <DollarSign className="w-4 h-4 text-orange-600" />
                      <span className="text-sm text-gray-900">{formatCurrency(forecast.prorated_revenue)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <DollarSign className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-gray-900">{formatCurrency(forecast.project_revenue)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <TrendingUp className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-semibold text-gray-900">
                        ${formatCurrency(forecast.total_revenue)}
                      </span>
                    </div>
                  </td>
                  {hasChurnAdjustment && (
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex flex-col items-end">
                        <span className="text-sm font-semibold text-orange-600">
                          ${formatCurrency(forecast.churn_adjusted_total_revenue || forecast.total_revenue)}
                        </span>
                        {(forecast.revenue_lost_to_churn || 0) > 0 && (
                          <span className="text-xs text-red-600">
                            (-${formatCurrency(forecast.revenue_lost_to_churn || 0)})
                          </span>
                        )}
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-sm text-green-600 font-medium">
                        ${formatCurrency(forecast.confirmed_revenue)}
                      </span>
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full transition-all"
                          style={{ width: `${confidenceLevel}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      {forecast.at_risk_revenue > 0 && (
                        <AlertCircle className="w-4 h-4 text-orange-600" />
                      )}
                      <span className={`text-sm font-medium ${
                        forecast.at_risk_revenue > 0 ? 'text-orange-600' : 'text-gray-400'
                      }`}>
                        ${formatCurrency(forecast.at_risk_revenue)}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2 border-gray-300">
            <tr>
              <td className="px-6 py-4 text-sm font-bold text-gray-900">Total</td>
              <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                ${formatCurrency(forecasts.reduce((sum, f) => sum + f.recurring_revenue, 0))}
              </td>
              <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                ${formatCurrency(forecasts.reduce((sum, f) => sum + f.prorated_revenue, 0))}
              </td>
              <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                ${formatCurrency(forecasts.reduce((sum, f) => sum + f.project_revenue, 0))}
              </td>
              <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                ${formatCurrency(forecasts.reduce((sum, f) => sum + f.total_revenue, 0))}
              </td>
              {hasChurnAdjustment && (
                <td className="px-6 py-4 text-right text-sm font-bold text-orange-600">
                  ${formatCurrency(forecasts.reduce((sum, f) => sum + (f.churn_adjusted_total_revenue || f.total_revenue), 0))}
                </td>
              )}
              <td className="px-6 py-4 text-right text-sm font-bold text-green-600">
                ${formatCurrency(forecasts.reduce((sum, f) => sum + f.confirmed_revenue, 0))}
              </td>
              <td className="px-6 py-4 text-right text-sm font-bold text-orange-600">
                ${formatCurrency(forecasts.reduce((sum, f) => sum + f.at_risk_revenue, 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default ForecastTable;